// ============================================================
// SALT — Available Matches Component
// Open matches to accept + user's pending matches
// Hides opponent identity for competitive integrity.
// Shows difficulty arrows for open matches relative to user's team.
// ============================================================

import React from 'react';
import type {
  OpenMatch,
  PendingMatch,
  UserTeamInfo,
} from '../../types/dashboard.types';
import {
  formatMatchType,
  formatMatchFormat,
  getTimeRemaining,
  formatScheduledTime,
} from './DashboardHelpers';

// ── Difficulty Indicator ─────────────────────────────────────
// Compares opponent points vs your points to show arrow indicators.
// Green up arrows = opponent is higher ranked (harder, more to gain)
// Red down arrows = opponent is lower ranked (easier, less to gain)
// Yellow dashes = similar skill level

type DifficultyLevel = 'up-3' | 'up-2' | 'up-1' | 'even' | 'down-1' | 'down-2' | 'down-3';

function getDifficultyLevel(
  opponentPoints: number,
  yourPoints: number
): DifficultyLevel {
  const diff = opponentPoints - yourPoints;
  const absDiff = Math.abs(diff);

  if (absDiff < 5) return 'even';
  if (absDiff < 12) return diff > 0 ? 'up-1' : 'down-1';
  if (absDiff < 25) return diff > 0 ? 'up-2' : 'down-2';
  return diff > 0 ? 'up-3' : 'down-3';
}

const DifficultyIndicator: React.FC<{ level: DifficultyLevel }> = ({ level }) => {
  if (level === 'even') {
    return (
      <span className="sl-am-difficulty sl-am-difficulty--even" title="Similar skill level">
        <span className="sl-am-diff-dash">―</span>
      </span>
    );
  }

  const isUp = level.startsWith('up');
  const count = parseInt(level.split('-')[1], 10);
  const colorClass = isUp ? 'sl-am-difficulty--up' : 'sl-am-difficulty--down';
  const title = isUp ? 'Higher rated opponent' : 'Lower rated opponent';
  const arrow = isUp ? '▲' : '▼';

  return (
    <span className={`sl-am-difficulty ${colorClass}`} title={title}>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="sl-am-diff-arrow">{arrow}</span>
      ))}
    </span>
  );
};

// ── Props ──────────────────────────────────────────────────

interface AvailableMatchesProps {
  openMatches: OpenMatch[];
  pendingMatches: PendingMatch[];
  loading: boolean;
  userTeam: UserTeamInfo | null;
  onMatchAction: (match: OpenMatch | PendingMatch) => void;
  onCancelMatch: (matchId: number) => void;
  onBrowseAll: () => void;
}

// ── Component ──────────────────────────────────────────────

const AvailableMatches: React.FC<AvailableMatchesProps> = ({
  openMatches,
  pendingMatches,
  loading,
  userTeam,
  onMatchAction,
  onCancelMatch,
  onBrowseAll,
}) => {
  // Combine pending first, then open, cap at 5
  const allMatches = [...pendingMatches, ...openMatches].slice(0, 5);

  // Check if any open (non-own) matches exist for showing the legend
  const hasOpenMatches = userTeam
    ? openMatches.some((m) => m.creatingTeam.teamId !== userTeam.teamId)
    : false;

  return (
    <section className="sl-card sl-available-matches">
      <div className="sl-card-header">
        <h2 className="sl-section-title">Available Matches</h2>
      </div>

      {loading ? (
        <div className="sl-available-matches-skeleton">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="sl-skeleton-row sl-skeleton-pulse" />
          ))}
        </div>
      ) : allMatches.length === 0 ? (
        <div className="sl-available-matches-empty">
          <div className="sl-available-matches-empty-title">
            No open matches right now
          </div>
          <div className="sl-available-matches-empty-text">
            Be the first to post an <strong>Available Now</strong> match or
            schedule a future series from the Create Match page.
          </div>
        </div>
      ) : (
        <>
          {/* Difficulty legend — only when user has a team and open matches exist */}
          {userTeam && hasOpenMatches && (
            <div className="sl-am-difficulty-legend">
              <span className="sl-am-legend-item sl-am-legend-item--up">
                <span className="sl-am-diff-arrow">▲</span> Higher Rated
              </span>
              <span className="sl-am-legend-sep">·</span>
              <span className="sl-am-legend-item sl-am-legend-item--even">
                <span className="sl-am-diff-dash">―</span> Even
              </span>
              <span className="sl-am-legend-sep">·</span>
              <span className="sl-am-legend-item sl-am-legend-item--down">
                <span className="sl-am-diff-arrow">▼</span> Lower Rated
              </span>
            </div>
          )}

          <div className="available-matches-list">
            {allMatches.map((match) => {
              const isPending = 'challengedTeam' in match;

              // Determine if this match belongs to the current user's team
              let isOwn = false;
              if (isPending) {
                isOwn = true;
              } else if (userTeam) {
                const open = match as OpenMatch;
                isOwn = open.creatingTeam.teamId === userTeam.teamId;
              }

              const displayLabel = isOwn ? 'Your Match' : 'Open Challenge';

              // Calculate difficulty for non-own open matches
              const difficulty =
                !isOwn && !isPending && userTeam
                  ? getDifficultyLevel(
                      (match as OpenMatch).creatingTeam.points,
                      userTeam.points
                    )
                  : null;

              return (
                <div key={match.matchId} className="available-match-row-wrap">
                  <button
                    className="available-match-row"
                    onClick={() => {
                      if (!isOwn && userTeam?.role === 'captain') onMatchAction(match);
                    }}
                    style={isOwn || userTeam?.role !== 'captain' ? { cursor: 'default' } : undefined}
                    title={
                      !isOwn && userTeam && userTeam.role !== 'captain'
                        ? 'Only the team captain can accept matches'
                        : undefined
                    }
                  >
                    <div className="available-match-main">
                      <span className="available-match-team-name">
                        {displayLabel}
                      </span>
                      <div className="available-match-meta-row">
                        <span className="available-match-meta">
                          {formatMatchType(match.matchType)} &middot;{' '}
                          {formatMatchFormat(match.matchFormat)}
                        </span>
                      </div>
                    </div>

                    {/* Right side: vertically centered in the full card */}
                    <div className="available-match-right">
                      {difficulty && <DifficultyIndicator level={difficulty} />}
                      {match.expiresAt && (
                        <span className="available-match-time-pill">
                          {getTimeRemaining(match.expiresAt)}
                        </span>
                      )}
                      {match.scheduledAt && !match.expiresAt && (
                        <span className="available-match-time-pill">
                          {formatScheduledTime(match.scheduledAt)}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Cancel stays outside the card */}
                  {isOwn && userTeam?.role === 'captain' && (
                    <button
                      className="sl-btn sl-btn-cancel-inline"
                      onClick={() => onCancelMatch(match.matchId)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Browse all link */}
      {!loading && (openMatches.length > 0 || pendingMatches.length > 0) && (
        <div className="sl-available-matches-footer">
          <button className="sl-link-button" onClick={onBrowseAll}>
            Browse all matches
          </button>
        </div>
      )}
    </section>
  );
};

export default AvailableMatches;